"use client";

import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { Loader2, Search, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  type MentionableSearchResult,
  searchMentionables,
} from "@/actions/mentions";
import { AppImage as Image } from "@/components/ui/app-image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MAX_TAGS_PER_IMAGE, type MediaTag } from "@/lib/media";

const clamp01 = (value: number) => (value < 0 ? 0 : value > 1 ? 1 : value);

type TagDisplay = {
  label: string;
  avatarUrl?: string | null;
  handle?: string | null;
};

type PersonResult = {
  id: string;
  label: string;
  handle: string | null;
  avatarUrl?: string | null;
};

type MediaTagEditorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  tags: MediaTag[];
  currentUserId?: string;
  onChange: (tags: MediaTag[]) => void;
};

/**
 * Full-screen people-tagging editor for a single composer image. Search and
 * add people via the inline search (works inside the dialog, unlike a popover),
 * then drag the pills to position them. Only the minimal { userId, x, y } is
 * emitted; display info is local-only.
 */
export function MediaTagEditor({
  open,
  onOpenChange,
  imageUrl,
  tags,
  currentUserId,
  onChange,
}: MediaTagEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [displayByUserId, setDisplayByUserId] = useState<
    Record<string, TagDisplay>
  >({});
  const [dragUserId, setDragUserId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const trimmedQuery = debouncedSearch.trim();

  const { data: results = [], isFetching } = useQuery<PersonResult[]>({
    queryKey: ["media-tag-people-search", currentUserId, trimmedQuery],
    enabled: open && trimmedQuery.length >= 1,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const result = await searchMentionables(trimmedQuery);
      if (!result.ok) {
        return [];
      }
      return result.data
        .filter(
          (item): item is MentionableSearchResult & { type: "user" } =>
            item.type === "user" && item.id !== currentUserId,
        )
        .map((item) => ({
          id: item.id,
          label: item.label,
          handle: item.handle,
          avatarUrl: item.avatarUrl,
        }));
    },
  });

  const taggedIds = new Set(tags.map((tag) => tag.userId));

  const handleAdd = (person: PersonResult) => {
    if (tags.length >= MAX_TAGS_PER_IMAGE) {
      toast.error(`You can tag up to ${MAX_TAGS_PER_IMAGE} people`);
      return;
    }
    if (taggedIds.has(person.id)) {
      return;
    }
    setDisplayByUserId((previous) => ({
      ...previous,
      [person.id]: {
        label: person.label,
        avatarUrl: person.avatarUrl,
        handle: person.handle,
      },
    }));
    const index = tags.length;
    const x = clamp01(0.5 + ((index % 3) - 1) * 0.14);
    const y = clamp01(0.4 + Math.floor(index / 3) * 0.16);
    onChange([...tags, { userId: person.id, x, y }]);
    setSearch("");
  };

  const handleRemove = (userId: string) => {
    onChange(tags.filter((tag) => tag.userId !== userId));
  };

  const moveTag = (clientX: number, clientY: number) => {
    if (!dragUserId || !containerRef.current) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const x = clamp01((clientX - rect.left) / rect.width);
    const y = clamp01((clientY - rect.top) / rect.height);
    onChange(
      tags.map((tag) => (tag.userId === dragUserId ? { ...tag, x, y } : tag)),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-[100dvw] flex-col gap-0 rounded-none border-0 p-0"
      >
        <DialogTitle className="sr-only">Tag people</DialogTitle>
        <header className="flex shrink-0 items-center justify-between border-b border-border px-4 pt-[max(env(safe-area-inset-top),0.75rem)] pb-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-text-secondary"
          >
            Cancel
          </Button>
          <h3 className="font-semibold text-text-primary">Tag people</h3>
          <Button
            type="button"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="rounded-full bg-brand px-4 font-semibold text-text-inverse hover:bg-brand-hover"
          >
            Done
          </Button>
        </header>

        <div className="flex flex-1 items-center justify-center overflow-hidden bg-black p-4">
          <div
            ref={containerRef}
            className="relative max-h-full w-full max-w-md touch-none select-none"
            style={{ aspectRatio: "4 / 5" }}
            onPointerMove={(event) => {
              if (dragUserId) {
                moveTag(event.clientX, event.clientY);
              }
            }}
            onPointerUp={() => setDragUserId(null)}
            onPointerLeave={() => setDragUserId(null)}
          >
            <Image
              src={imageUrl}
              alt="Tag people"
              fill
              className="rounded-xl object-contain"
              sizes="(max-width: 768px) 100vw, 480px"
            />
            {tags.map((tag) => {
              const display = displayByUserId[tag.userId];
              return (
                <div
                  key={tag.userId}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${tag.x * 100}%`, top: `${tag.y * 100}%` }}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    setDragUserId(tag.userId);
                  }}
                >
                  <span className="flex cursor-grab items-center gap-1.5 rounded-full bg-black/80 py-1 pr-1 pl-1.5 text-xs text-white shadow-lg backdrop-blur-md active:cursor-grabbing">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={display?.avatarUrl ?? undefined} />
                      <AvatarFallback className="bg-white/20 text-[9px] text-white">
                        {(display?.label ?? "?").slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="max-w-[120px] truncate font-medium">
                      {display?.label ?? "Tagged"}
                    </span>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemove(tag.userId);
                      }}
                      onPointerDown={(event) => event.stopPropagation()}
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
                      aria-label="Remove tag"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <footer className="relative shrink-0 border-t border-border px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-text-secondary">
              {tags.length}/{MAX_TAGS_PER_IMAGE} tagged
            </p>
            <p className="text-xs text-text-tertiary">
              Drag pills to reposition
            </p>
          </div>

          {/* Results appear above the input */}
          {trimmedQuery.length >= 1 ? (
            <div className="absolute right-4 bottom-full left-4 mb-2 max-h-64 overflow-y-auto rounded-2xl border border-border bg-surface shadow-2xl">
              {isFetching && results.length === 0 ? (
                <div className="flex items-center justify-center gap-2 p-4 text-sm text-text-secondary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="p-4 text-center text-sm text-text-secondary">
                  No people found
                </div>
              ) : (
                results.map((person) => {
                  const alreadyTagged = taggedIds.has(person.id);
                  return (
                    <button
                      key={person.id}
                      type="button"
                      disabled={alreadyTagged}
                      onClick={() => handleAdd(person)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-surface-alt disabled:opacity-50"
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={person.avatarUrl ?? undefined} />
                        <AvatarFallback className="text-xs">
                          {person.label.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-text-primary">
                          {person.label}
                        </span>
                        {person.handle ? (
                          <span className="truncate text-xs text-text-secondary">
                            @{person.handle}
                          </span>
                        ) : null}
                      </div>
                      {alreadyTagged ? (
                        <span className="text-xs text-text-tertiary">
                          Tagged
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}

          <label className="flex items-center gap-2 rounded-full border border-border bg-background px-3.5 py-2.5">
            <Search className="h-4 w-4 shrink-0 text-text-tertiary" />
            <span className="sr-only">Search people to tag</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search people to tag…"
              className="min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-text-tertiary hover:text-text-primary"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </label>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
